import { useEffect, useState, useCallback } from "react";
import { BookOpen, Trash2, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LoteMemoriasPanel from "@/components/LoteMemoriasPanel";

/**
 * Pantalla de gestión del diccionario de mapeos referencia_excel → familia_sistema.
 *
 * Cada vez que en el paso 3 del wizard de contenedor el director acepta una
 * fusión entre una referencia detectada del Excel y una familia existente en el
 * catálogo, se guarda un mapeo aquí. En importaciones futuras el sistema
 * pre-selecciona ese match automáticamente.
 */
export default function MapeosReferenciaPage() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/productos/referencia-mapeos");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el diccionario");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const borrar = async (id) => {
    if (!window.confirm("¿Borrar este mapeo? El sistema dejará de pre-aplicarlo en importaciones futuras.")) return;
    try {
      await api.delete(`/productos/referencia-mapeos/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Mapeo eliminado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar");
    }
  };

  const filtrados = items.filter((m) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      (m.referencia_excel || "").toLowerCase().includes(t)
      || (m.familia_key_sistema || "").toLowerCase().includes(t)
      || (m.nombre_representativo || "").toLowerCase().includes(t)
    );
  });

  return (
    <div className="space-y-4" data-testid="mapeos-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-display font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-primary" /> Diccionario de referencias
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Cada vez que fusionas una referencia del Excel con una familia del catálogo en el wizard de contenedor,
            se guarda aquí. En próximas importaciones el sistema aplicará automáticamente estos mapeos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="pl-9 h-9 w-56" data-testid="mapeos-buscar" />
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="mapeos-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-10 text-center">Cargando…</div>
      ) : filtrados.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center">
          <BookOpen className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
          <div className="text-sm font-medium text-muted-foreground">
            {items.length === 0 ? "Aún no hay mapeos guardados." : "Ningún mapeo coincide con la búsqueda."}
          </div>
          <div className="text-xs text-muted-foreground/70 mt-1">
            Los mapeos se crean automáticamente al confirmar el paso 3 del wizard de contenedor.
          </div>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-muted">
              <tr className="text-left text-[11px] uppercase font-bold text-muted-foreground">
                <th className="px-3 py-3">Referencia del Excel</th>
                <th className="px-3 py-3">Familia del sistema</th>
                <th className="px-3 py-3 text-center">Foto</th>
                <th className="px-3 py-3 text-right">Variantes</th>
                <th className="px-3 py-3 text-right">Usos</th>
                <th className="px-3 py-3">Última vez</th>
                <th className="px-3 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((m) => (
                <tr key={m.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`mapeo-row-${m.id}`}>
                  <td className="px-3 py-2 font-mono text-xs text-foreground/80">{m.referencia_excel}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{m.nombre_representativo || m.familia_key_sistema}</div>
                    <div className="text-[10px] font-mono text-muted-foreground/80">{m.familia_key_sistema}</div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {m.foto_producto_id ? (
                      <img src={`${API}/api/catalogo/img/${m.foto_producto_id}?w=80`} alt="" className="w-10 h-10 object-cover rounded mx-auto" loading="lazy" />
                    ) : (
                      <span className="text-muted-foreground/40 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{m.variantes_count || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{m.usos || 1}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {m.ultima_vez ? new Date(m.ultima_vez).toLocaleDateString("es-CO", { year: "numeric", month: "short", day: "numeric" }) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => borrar(m.id)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`mapeo-del-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoteMemoriasPanel />
    </div>
  );
}
